/**
 * Returns the character offset in `textarea.value` that corresponds to the
 * given viewport coordinates. Uses a hidden mirror div so the browser's own
 * text layout is used for measurement — no canvas math needed.
 */
export function caretOffsetFromPoint(
  textarea: HTMLTextAreaElement,
  clientX: number,
  clientY: number,
): number {
  const cs = window.getComputedStyle(textarea)
  const rect = textarea.getBoundingClientRect()

  const div = document.createElement('div')
  div.style.cssText = [
    `font-family:${cs.fontFamily}`,
    `font-size:${cs.fontSize}`,
    `font-weight:${cs.fontWeight}`,
    `letter-spacing:${cs.letterSpacing}`,
    `line-height:${cs.lineHeight}`,
    `padding-top:${cs.paddingTop}`,
    `padding-right:${cs.paddingRight}`,
    `padding-bottom:${cs.paddingBottom}`,
    `padding-left:${cs.paddingLeft}`,
    `border-top:${cs.borderTopWidth} solid transparent`,
    `border-right:${cs.borderRightWidth} solid transparent`,
    `border-bottom:${cs.borderBottomWidth} solid transparent`,
    `border-left:${cs.borderLeftWidth} solid transparent`,
    `box-sizing:${cs.boxSizing}`,
    `white-space:pre-wrap`,
    `word-break:break-word`,
    `overflow:hidden`,
    `position:absolute`,
    `visibility:hidden`,
    `pointer-events:none`,
    `top:${rect.top + window.scrollY}px`,
    `left:${rect.left + window.scrollX}px`,
    `width:${rect.width}px`,
    `height:${rect.height}px`,
  ].join(';')

  const text = textarea.value
  for (let i = 0; i <= text.length; i++) {
    const span = document.createElement('span')
    const ch = text[i]
    if (ch === '\n') {
      span.textContent = '​'
      div.appendChild(span)
      div.appendChild(document.createElement('br'))
    } else {
      span.textContent = i < text.length ? ch : '​'
    }
    span.dataset.i = String(i)
    if (ch !== '\n') div.appendChild(span)
  }

  document.body.appendChild(div)

  let best = text.length
  let bestDist = Infinity
  div.querySelectorAll<HTMLSpanElement>('span[data-i]').forEach((span) => {
    const r = span.getBoundingClientRect()
    const midX = (r.left + r.right) / 2
    const midY = (r.top + r.bottom) / 2
    const dist = (clientX - midX) ** 2 + ((clientY - midY) * 2) ** 2
    if (dist < bestDist) {
      bestDist = dist
      const i = Number(span.dataset.i)
      best = clientX >= midX ? Math.min(i + 1, text.length) : i
    }
  })

  document.body.removeChild(div)
  return best
}
